import React, { useState } from 'react';
import { Copy } from 'lucide-react';

const BetCard = ({ bet, onUpdate, showPrivate, user, onCopyBet }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [closingLine, setClosingLine] = useState(bet.closingLine || '');
  const [editingClosingLine, setEditingClosingLine] = useState(false);

  const potentialReturn = bet.stake 
    ? (parseFloat(bet.stake) * parseFloat(bet.odds)).toFixed(2)
    : `${(parseFloat(bet.units || 1) * parseFloat(bet.odds)).toFixed(2)} units`;

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'bg-green-500';
      case 'lost': return 'bg-red-500';
      case 'push': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleClosingLineUpdate = () => {
    onUpdate(bet.id, { closingLine });
    setEditingClosingLine(false);
  };

  if (!bet.isPublic && !showPrivate) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-green-600">{bet.sport}</span>
            {bet.league && (
              <span className="text-sm text-gray-500">• {bet.league}</span>
            )}
            <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(bet.confidence)}`}>
              {bet.confidence} confidence
            </span>
            {!bet.isPublic && (
              <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                Private
              </span>
            )}
            {bet.placedOnTime === false && (
              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                Late
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-800">{bet.match}</h3>
          <p className="text-gray-600">{bet.bet}</p>
          {bet.eventDateTime && (
            <p className="text-xs text-gray-500 mt-1">Event: {formatDate(bet.eventDateTime)}</p>
          )}
        </div>
        <div className={`w-3 h-3 rounded-full ${getStatusColor(bet.status)}`} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm mb-2">
        <div>
          <span className="text-gray-500">Odds:</span>
          <span className="ml-1 font-medium">{bet.odds}</span>
          {bet.closingLine && (
            <span className="block text-xs text-gray-500">CL: {bet.closingLine}</span>
          )}
        </div>
        <div>
          <span className="text-gray-500">Units:</span>
          <span className="ml-1 font-medium">{bet.units || '1'}</span>
          {bet.stake && (
            <span className="block text-xs text-gray-500">${bet.stake}</span>
          )}
        </div>
        <div>
          <span className="text-gray-500">Return:</span>
          <span className="ml-1 font-medium">{potentialReturn}</span>
        </div>
      </div>

      {bet.tipster && (
        <p className="text-sm text-gray-500 mb-2">
          {bet.isTipsterBet ? 'Tipster' : 'From'}: {bet.tipster}
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-green-600 hover:text-green-700"
        >
          {showDetails ? 'Hide' : 'Show'} Details
        </button>
        {bet.isTipsterBet && user && user.uid !== bet.tipsterUid && onCopyBet && (
          <button
            onClick={() => onCopyBet(bet)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Copy size={14} />
            Copy Bet
          </button>
        )}
      </div>

      {showDetails && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          {bet.datePlaced && (
            <p className="text-xs text-gray-500 mb-2">Placed: {formatDate(bet.datePlaced)}</p>
          )}
          {(user?.uid === bet.tipsterUid || user?.email === bet.userEmail) && (
            <div className="mb-3">
              <label className="text-sm font-medium text-gray-700 block mb-1">Closing Line</label>
              {editingClosingLine ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={closingLine}
                    onChange={(e) => setClosingLine(e.target.value)}
                    className="flex-1 p-1 text-sm border border-gray-300 rounded"
                    placeholder="e.g., 1.85"
                  />
                  <button
                    onClick={handleClosingLineUpdate}
                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingClosingLine(false);
                      setClosingLine(bet.closingLine || '');
                    }}
                    className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingClosingLine(true)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {bet.closingLine ? `Edit (${bet.closingLine})` : 'Add closing line'}
                </button>
              )}
            </div>
          )}
          {(user?.uid === bet.tipsterUid || user?.email === bet.userEmail) && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => onUpdate(bet.id, { status: 'won' })}
                className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
              >
                Won
              </button>
              <button
                onClick={() => onUpdate(bet.id, { status: 'lost' })}
                className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
              >
                Lost
              </button>
              <button
                onClick={() => onUpdate(bet.id, { status: 'push' })}
                className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Push
              </button>
              <button
                onClick={() => onUpdate(bet.id, { status: 'pending' })}
                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Pending
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BetCard;
